




















































import { Component, Inject, Prop, Watch } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import { getModule } from "vuex-module-decorators";

import FormField from "@/lib/components/FormField.vue";
import FormErrors from "@/lib/components/FormErrors.vue";
import SubmitButton from "@/lib/components/SubmitButton.vue";

import dataStore from "@/store";
import IdGenerator from "@/lib/services/id-generator";
import GeneralFormMixin from "@/lib/mixins/GeneralForm";

import BulkShipmentStore from "../store";
import ShippingManifest from "../shipping-manifest.model";

@Component({
  components: {
    FormField,
    FormErrors,
    SubmitButton,
  },
})
export default class ShippingManifestCreateForm extends mixins(
  GeneralFormMixin
) {
  @Prop({ required: true })
  public readonly factoryId!: string;

  @Prop({ required: true })
  public readonly bulkShipmentsIds!: string[];

  @Prop({ default: "Save" })
  public readonly buttonText!: string;

  @Inject("IdGenerator")
  private fIdGenerator!: IdGenerator;

  public carrier?: string;
  public notes?: string;
  public trackingNumber?: string;

  private fBulkShipmentStore: BulkShipmentStore;

  private get sortedBulkShipmentIdsByCreatedAtAsc(): string[] {
    return Array.from(this.bulkShipmentsIds).sort((a, b) => {
      const shipmentA = this.fBulkShipmentStore.getBulkShipmentById(a);
      const shipmentB = this.fBulkShipmentStore.getBulkShipmentById(b);

      const createdDateA = shipmentA?.createdAt?.getTime();
      const createdDateB = shipmentB?.createdAt?.getTime();

      if (createdDateB === createdDateA) {
        return 0;
      }

      if (!createdDateB || !createdDateA) {
        return createdDateA ? 1 : -1;
      }

      return createdDateA > createdDateB ? 1 : -1;
    });
  }

  private get firstBulkShipmentTrackingNumber(): string | undefined {
    for (const id of this.sortedBulkShipmentIdsByCreatedAtAsc) {
      const bulkShipment = this.fBulkShipmentStore.getBulkShipmentById(id);

      if (!bulkShipment?.trackingNumber) {
        continue;
      }

      return bulkShipment.trackingNumber;
    }
  }

  private get firstBulkShipmentCarrier(): string | undefined {
    for (const id of this.sortedBulkShipmentIdsByCreatedAtAsc) {
      const bulkShipment = this.fBulkShipmentStore.getBulkShipmentById(id);

      if (!bulkShipment?.carrier) {
        continue;
      }

      return bulkShipment.carrier;
    }
  }

  constructor() {
    super();
    this.fBulkShipmentStore = getModule(BulkShipmentStore, dataStore);
  }

  public onCancelClick(): void {
    this.$emit("cancel");
  }

  protected data(): Record<string, unknown> {
    return {
      carrier: this.carrier,
      notes: this.notes,
      trackingNumber: this.trackingNumber,
    };
  }

  protected async performSubmit(): Promise<void> {
    const shippingManifest = this.generateShippingManifestObject();

    const savedManifest = await this.fBulkShipmentStore.saveShippingManifest(
      shippingManifest
    );

    this.$emit("created", savedManifest);
  }

  private async loadData(): Promise<void> {
    if (this.bulkShipmentsIds.length === 0) {
      return;
    }

    await this.fBulkShipmentStore.loadBulkShipmentsByIds({
      ids: this.bulkShipmentsIds,
    });
  }

  private prefillCarrier(): void {
    if (this.carrier) {
      return;
    }

    this.carrier = this.firstBulkShipmentCarrier;
  }

  private prefillTrackingNumber(): void {
    if (this.trackingNumber) {
      return;
    }

    this.trackingNumber = this.firstBulkShipmentTrackingNumber;
  }

  private generateShippingManifestObject(): ShippingManifest {
    if (!this.trackingNumber) {
      throw new Error("Tracking number is required!");
    }

    if (!this.carrier) {
      throw new Error("Carrier is required!");
    }

    const id = this.fIdGenerator.getId();

    return new ShippingManifest(
      id,
      this.factoryId,
      this.trackingNumber,
      this.bulkShipmentsIds,
      this.carrier,
      this.notes
    );
  }

  @Watch("bulkShipmentsIds", { immediate: true })
  private async _onBulkShipmentsIdsChange(): Promise<void> {
    await this.loadData();
    this.prefillTrackingNumber();
    this.prefillCarrier();
  }
}
